/**
 * CUSTOMER FIX Stage 7 — persisted cart revalidation (price + availability).
 * Local cart snapshots are not authoritative — compare against live catalog products.
 * Dual-currency: compare/sync via ProductPrice rows matching session currency.
 */

import { resolveCatalogUnitPriceMajor } from 'pi-kiosk-shared';
import type { CatalogProduct, ShopCart, ShopCartLine } from '../types.js';
import { setLineUnitPrice } from './cartLogic.js';
import { computeCartStaleState } from '../presenters/shopCheckoutPresenter.js';
import { SHOP_CURRENCY_FALLBACK } from './money.js';

export interface CartRevalidationLineState {
  readonly key: string;
  readonly unavailable: boolean;
  readonly priceChanged: boolean;
  readonly storedUnitPrice: number;
  readonly currentUnitPrice: number | null;
  readonly needsConfiguration: boolean;
}

export interface CartRevalidationState {
  readonly lines: readonly CartRevalidationLineState[];
  readonly staleLineKeys: ReadonlySet<string>;
  readonly priceChangedLineKeys: ReadonlySet<string>;
  readonly needsConfigurationLineKeys: ReadonlySet<string>;
  readonly hasBlockingIssues: boolean;
}

export interface ReVerifyAfterExpiryResult extends CartRevalidationState {
  /** True when the hold clock has elapsed (cart retained; availability re-checked). */
  readonly expired: boolean;
  /** Line keys that remain in the cart after expiry (retain policy — never auto-cleared). */
  readonly retainedLineKeys: readonly string[];
}

/**
 * P5 A4 — after low-stock hold TTL expires: keep cart lines, re-verify availability
 * against live catalog (price + stock). Activity must not extend TTL (caller clock).
 */
export function reVerifyAfterExpiry(
  cartLines: readonly ShopCartLine[],
  products: readonly CatalogProduct[] | undefined,
  reservationExpiresAt: string | null | undefined,
  options?: {
    readonly nowMs?: number;
    readonly sessionCurrency?: string | null;
  },
): ReVerifyAfterExpiryResult {
  const nowMs = options?.nowMs ?? Date.now();
  const end =
    typeof reservationExpiresAt === 'string' && reservationExpiresAt.trim().length > 0
      ? Date.parse(reservationExpiresAt)
      : Number.NaN;
  const expired = Number.isFinite(end) && end <= nowMs;
  const state = computeCartRevalidationState(
    cartLines,
    products,
    options?.sessionCurrency,
  );
  return {
    ...state,
    expired,
    retainedLineKeys: cartLines.map((line) => line.key),
  };
}

function resolveSessionCurrency(sessionCurrency: string | null | undefined): string {
  const trimmed =
    typeof sessionCurrency === 'string' ? sessionCurrency.trim().toUpperCase() : '';
  return trimmed.length > 0 ? trimmed : SHOP_CURRENCY_FALLBACK;
}

function resolveCatalogUnitPrice(
  line: ShopCartLine,
  products: readonly CatalogProduct[] | undefined,
  sessionCurrency: string | null | undefined,
): number | null {
  if (products === undefined) {
    return null;
  }
  const product = products.find((item) => item.id === line.product.id);
  if (product === undefined) {
    return null;
  }
  const currency = resolveSessionCurrency(sessionCurrency);
  if (line.variant !== null) {
    const variant = product.variants?.find((item) => item.id === line.variant?.id);
    if (variant === undefined) {
      return null;
    }
    return resolveCatalogUnitPriceMajor(
      { price: variant.price, prices: variant.prices },
      currency,
    );
  }
  return resolveCatalogUnitPriceMajor(product, currency);
}

function variantStillValid(
  line: ShopCartLine,
  products: readonly CatalogProduct[] | undefined,
): boolean {
  if (line.variant === null) {
    return true;
  }
  if (products === undefined) {
    return false;
  }
  const product = products.find((item) => item.id === line.product.id);
  if (product === undefined) {
    return false;
  }
  return (
    product.variants?.some((variant) => variant.id === line.variant?.id) === true
  );
}

export function computeCartRevalidationState(
  cartLines: readonly ShopCartLine[],
  products: readonly CatalogProduct[] | undefined,
  sessionCurrency?: string | null,
): CartRevalidationState {
  const stale = computeCartStaleState(cartLines, products);
  const priceChangedLineKeys = new Set<string>();
  const needsConfigurationLineKeys = new Set<string>();
  const lines: CartRevalidationLineState[] = [];

  for (const line of cartLines) {
    const currentUnitPrice = resolveCatalogUnitPrice(line, products, sessionCurrency);
    const needsConfiguration =
      line.variant !== null && !variantStillValid(line, products);
    const priceChanged =
      currentUnitPrice !== null &&
      Math.abs(currentUnitPrice - line.unitPrice) > 0.000_001;
    if (priceChanged) {
      priceChangedLineKeys.add(line.key);
    }
    if (needsConfiguration) {
      needsConfigurationLineKeys.add(line.key);
    }
    lines.push({
      key: line.key,
      unavailable: stale.staleLineKeys.has(line.key),
      priceChanged,
      storedUnitPrice: line.unitPrice,
      currentUnitPrice,
      needsConfiguration,
    });
  }

  const hasBlockingIssues =
    stale.staleLineKeys.size > 0 ||
    priceChangedLineKeys.size > 0 ||
    needsConfigurationLineKeys.size > 0;

  return {
    lines,
    staleLineKeys: stale.staleLineKeys,
    priceChangedLineKeys,
    needsConfigurationLineKeys,
    hasBlockingIssues,
  };
}

/** Sync cart line unit prices to authoritative catalog values (Stage 7). */
export function applyAuthoritativePricesToCart(
  cart: ShopCart,
  products: readonly CatalogProduct[] | undefined,
  lineKeys?: ReadonlySet<string>,
  sessionCurrency?: string | null,
): ShopCart {
  let next = cart;
  for (const line of cart.lines) {
    if (lineKeys !== undefined && !lineKeys.has(line.key)) {
      continue;
    }
    const currentUnitPrice = resolveCatalogUnitPrice(line, products, sessionCurrency);
    if (
      currentUnitPrice !== null &&
      Math.abs(currentUnitPrice - line.unitPrice) > 0.000_001
    ) {
      next = setLineUnitPrice(next, line.key, currentUnitPrice);
    }
  }
  return next;
}
